/**
 * API types and interfaces for SportForm AI
 * These types define the contract between frontend and backend services
 */

import type {
  TrajectoryConfig,
  TrajectoryGoalResult,
  TravelTargetSource,
  TravelComparisonOperator,
  BodyHeightDefinition,
} from './trajectory';
// Goal Score (PR-7a) — new types live in their own file per the delivery
// plan's parallel-build discipline; see types/goalScore.ts for the contract.
import type { GoalScoreItem } from './goalScore';

// ============================================================================
// Functional Landmark Constants
// ============================================================================

/**
 * Functional landmark names that auto-resolve based on athlete handedness at
 * evaluation time. These mirror the backend's FUNCTIONAL_LANDMARKS mapping in
 * functional_landmark_definitions.py.
 */
export const FUNCTIONAL_LANDMARK_NAMES: string[] = [
  'lead_wrist', 'trail_wrist', 'lead_elbow', 'trail_elbow',
  'lead_shoulder', 'trail_shoulder', 'lead_hip', 'trail_hip',
  'lead_knee', 'trail_knee', 'lead_ankle', 'trail_ankle',
  'lead_heel', 'trail_heel', 'lead_foot_index', 'trail_foot_index',
];

/** Functional landmarks grouped by body region, for picker UI display */
export const FUNCTIONAL_LANDMARK_GROUPS: Record<string, string[]> = {
  Arm: ['lead_wrist', 'trail_wrist', 'lead_elbow', 'trail_elbow', 'lead_shoulder', 'trail_shoulder'],
  Leg: ['lead_hip', 'trail_hip', 'lead_knee', 'trail_knee', 'lead_ankle', 'trail_ankle'],
  Foot: ['lead_heel', 'trail_heel', 'lead_foot_index', 'trail_foot_index'],
};

/**
 * Virtual / computed landmark names. These are midpoint and derived landmarks
 * that the backend computes from pairs of anatomical landmarks at evaluation
 * time. They are valid in AxisConfig.landmark_start / landmark_end fields.
 *
 * Grouped for display in the picker UI.
 */
export const VIRTUAL_LANDMARK_GROUPS: Record<string, Array<{ value: string; label: string }>> = {
  "Body Center": [
    { value: "mid_shoulder", label: "Mid Shoulder" },
    { value: "mid_hip", label: "Mid Hip" },
    { value: "torso_center", label: "Torso Center" },
    { value: "mid_knee", label: "Mid Knee" },
    { value: "mid_ankle", label: "Mid Ankle" },
    { value: "mid_ear", label: "Mid Ear" },
  ],
  "Lead Side (Computed)": [
    { value: "lead_mid_arm", label: "Lead Mid-Arm" },
    { value: "lead_mid_leg", label: "Lead Mid-Leg" },
  ],
  "Trail Side (Computed)": [
    { value: "trail_mid_arm", label: "Trail Mid-Arm" },
    { value: "trail_mid_leg", label: "Trail Mid-Leg" },
  ],
};

/**
 * Returns true if the landmark name uses a functional (handedness-aware) prefix.
 * Functional names are stored as-is in configs; the backend resolves them at
 * evaluation time based on detected athlete lead side.
 */
export function isFunctionalLandmark(name: string): boolean {
  return name.startsWith('lead_') || name.startsWith('trail_');
}

// ============================================================================
// Video Types
// ============================================================================
export interface Video {
  id: string;
  filename: string;
  file_path?: string;
  uploaded_at?: string;
  duration_seconds?: number;
  duration?: number; // Backend returns this instead of duration_seconds
  frame_count?: number;
  fps?: number;
  width?: number;
  height?: number;
  file_size?: number;
}

export interface VideoApiResponse {
  video_id: string;
  filename: string;
  width?: number;
  height?: number;
  fps?: number;
  frame_count?: number;
  duration?: number;
  file_size?: number;
}

export interface VideoUploadRequest {
  file: File;
  onProgress?: (progress: number) => void;
}

export interface VideoUploadResponse {
  message: string;
  video: VideoApiResponse;
  pose_extraction: {
    success: boolean;
    frames_processed: number;
  };
  video_id?: string;
  filename?: string;
  file_path?: string;
}

// ============================================================================
// Annotation Types
// ============================================================================
export interface Region {
  id: string;
  name: string;
  type: 'circle' | 'polygon' | 'body_region';
  landmark_indices: number[];
  coordinates?: {
    x: number;
    y: number;
    radius?: number; // for circles
    points?: Array<{ x: number; y: number }>; // for polygons
  };
  importance?: 'low' | 'medium' | 'high' | 'critical';
  target_phase?: string | null; // Phase label this region is specific to (null = global)
  activeFrames?: { start: number; end: number } | null;
  spatial_goal?: SpatialGoal; // Optional spatial goal for this region
  movement_goal?: TrajectoryConfig; // Optional movement goal (trajectory) for this region
  timing_goal?: TimingConfig; // Optional timing goal (inter-landmark timing coordination)
}

export interface Annotation {
  video_id: string;
  regions: Region[];
  reference_frame?: number;
  created_at?: string;
  updated_at?: string;
}

export interface AnnotationRequest {
  video_id: string;
  regions: Region[];
  reference_frame?: number;
}

export interface AnnotationResponse {
  message: string;
  annotation: Annotation;
}

// ============================================================================
// Phase Types
// ============================================================================
export interface Phase {
  id: string;
  name: string;
  description?: string;
  start_frame: number;
  end_frame: number;
  duration_frames: number;
  importance?: 'low' | 'medium' | 'high' | 'critical';
  key_characteristics?: Record<string, unknown>;
}

export interface PhaseDetectionRequest {
  video_id: string;
  pose_data: PoseData[];
  min_phase_duration?: number;
}

export interface PhaseDetectionResponse {
  video_id: string;
  phases: Phase[];
  total_frames: number;
  detection_method: string;
}

// ============================================================================
// Phase Mapping (for phase-aligned video playback)
// ============================================================================
export interface PhaseInfo {
  phase_id: string;
  label: string;
  phase_type: 'transition' | 'hold' | 'dynamic';
  start_frame: number;
  end_frame: number;
  importance: 'critical' | 'high' | 'medium' | 'low';
  duration_frames: number;
}

export interface PhasePair {
  template_phase: PhaseInfo;
  athlete_phase: PhaseInfo;
  timing_ratio: number;
  timing_score: number;
  pose_score: number;
}

export interface PhaseMapping {
  phase_pairs: PhasePair[];
  unmapped_template_phases: PhaseInfo[];
  unmapped_athlete_phases: PhaseInfo[];
  overall_timing_score: number;
  overall_pose_score: number;
  num_matched_phases: number;
  num_unmapped_template_phases?: number;
  num_unmapped_athlete_phases?: number;
}

// ============================================================================
// Template Types
// ============================================================================
export interface MovementTemplate {
  id: string;
  name: string;
  description?: string;
  movement_type?: string;
  sport_category?: string;
  athlete_level?: string;
  coaching_cues?: string[];
  common_mistakes?: string[];
  reference_video_id: string;
  regions?: Region[];
  phases?: Phase[];
  annotations?: Annotation[];
  created_at?: string;
  updated_at?: string;
  finalized?: boolean;
  finalized_at?: string;
  finalized_by?: string;
  coach_notes?: string;
  /** Whether the movement has a clear lead side (lunge, throw), is bilateral (squat, deadlift), or is rotational (golf swing, discus throw). */
  exercise_symmetry?: 'asymmetric' | 'symmetric' | 'rotational';
  /** Which side leads in the reference video. Meaningful when exercise_symmetry === 'asymmetric' or 'rotational'. */
  template_lead_side?: 'left' | 'right' | null;
  /**
   * When true, the evaluation pipeline adds coaching tips about the ORDER body segments
   * fire (e.g. "core before arm", "hip before knee"). Opt-in, default false.
   * Only affects coaching narrative — never changes the numeric score.
   */
  enable_sequencing_coaching?: boolean;
  /** Optional YouTube reference video URL attached to this template (YTI-1). */
  youtube_url?: string | null;
  /** Channel this template belongs to, if any. Populated by Phase A backend changes. */
  channel_id?: string | null;
  /** Slug of the channel this template belongs to. Used to scope per-template athlete queries. */
  channel_slug?: string | null;
  /** Display name of the channel this template belongs to. Preferred over channel_slug for display. */
  channel_name?: string | null;
  /**
   * Template visibility (Security P2-1).
   * "private" (default): only owner/supporters/channel-members/admin can access.
   * "public": anyone including unauthenticated callers can read this template.
   */
  visibility?: 'private' | 'public';
  /**
   * Archive state (D12). Archived templates are excluded from the active-template
   * cap and hidden from the default template list. Sourced from TemplateIndex.
   */
  is_archived?: boolean;
  /**
   * Teaching video linked to this template via POST /api/templates/{id}/teaching-video.
   * Injected by the backend get_template/list_templates endpoints from TemplateIndex.
   */
  teaching_video_id?: string | null;
  teaching_video_source_type?: 'video' | 'audio' | 'paste' | null;
  /**
   * Template kind (PTD-1/PTD-2).
   * 'solo' (default): standard single-person template.
   * 'positional': multi-figure template for team drills.
   * Absent/undefined on legacy templates created before PTD-1 — treat as 'solo'.
   */
  template_kind?: 'solo' | 'positional';
  metadata?: {
    duration_seconds?: number;
    total_frames?: number;
    fps?: number;
    sport_category?: string;
    athlete_level?: string;
    coaching_cues?: string[];
    common_mistakes?: string[];
    camera_device?: string;
    camera_orientation?: string;
    camera_angle_setup?: string;
    /**
     * Auto-detected camera view ("side" | "frontal" | "diagonal"), written at
     * template finalization. The coach-set camera_angle_setup takes priority
     * (mirrors backend resolve_effective_camera_view()).
     */
    camera_view?: string;
    performance_notes?: string[];
  };
}

export interface CreateTemplateRequest {
  name: string;
  description?: string;
  movement_type?: string;
  reference_video_id: string;
  regions: Region[];
  phases: Phase[];
  coaching_cues?: string[];
  common_mistakes?: string[];
  /** Optional YouTube reference video URL (YTI-1). */
  youtube_url?: string | null;
}

export interface CreateTemplateResponse {
  message: string;
  template: MovementTemplate;
}

// ============================================================================
// Pose Data Types
// ============================================================================
export interface Landmark {
  x: number;
  y: number;
  z: number;
  visibility: number;
}

export interface PoseData {
  frame_number: number;
  timestamp: number;
  landmarks: Landmark[];
}

/**
 * A single "raw" pose frame as returned by the GET /videos/{id}/pose endpoint
 * at the axios call boundary (typed `any[]` there — see videoApi.ts). This is
 * the loose shape actually read by client-side helpers that only need
 * per-landmark x/y/z/visibility (goalOverlayHelpers.ts, usePoseExtraction.ts's
 * extractLandmarksFromFrame): unlike `PoseData` above it does not require
 * `frame_number` / `timestamp`, and `landmarks` itself is optional to tolerate
 * in-flight/partial payloads.
 *
 * Structurally compatible with (but intentionally not merged into) the
 * independently-defined loose pose-frame shapes in
 * `EnhancedEvaluationDisplay.tsx` (`PoseFrame`) and `utils/landmarkDetection.ts`
 * (`PoseData`) — keep this in sync if those shapes change.
 */
export interface RawPoseFrame {
  landmarks?: Array<{ x: number; y: number; z?: number; visibility?: number }>;
}

export interface PoseExtractionRequest {
  video_id: string;
}

export interface PoseExtractionResponse {
  video_id: string;
  pose_data: PoseData[];
  total_frames: number;
  message: string;
}

// ============================================================================
// 3D Pose Data Types
// ============================================================================

/** A single 3D landmark position in world/gravity-aligned coordinates */
export interface Landmark3D {
  x: number;
  y: number;
  z: number;
}

/**
 * Response shape for the /videos/{id}/pose?include_3d=true endpoint.
 * landmarks_3d is a (T, 17, 3) array in H36M joint order when 3D lifting
 * was performed during pose extraction; null otherwise.
 */
export interface PoseData3DResponse {
  frames: RawPoseFrame[];
  landmarks_3d: number[][][] | null;
  lifting_model: string | null;
  is_gravity_aligned: boolean | null;
}

// ============================================================================
// Gemini Vision Observation Types
// ============================================================================

/** A single frame-level callout produced by the Gemini vision model */
export interface GeminiFrameCallout {
  frame_index: number;
  observation: string;
}

/**
 * A structured error observation produced by Gemini Vision.
 * These are generated by analyzing video frames against scoring goal failures.
 */
export interface GeminiErrorObservation {
  error_type: string;
  /** The goal key that triggered this observation, or null for holistic observations */
  source_goal: string | null;
  visual_description: string;
  severity: 'critical' | 'significant' | 'minor';
  frame_callouts: GeminiFrameCallout[];
  coaching_suggestion: string;
}

// ============================================================================
// Evaluation Types
// ============================================================================

// Error severity levels based on score ranges
export type ErrorSeverity = 'excellent' | 'minor' | 'moderate' | 'significant' | 'critical';

// Tempo comparison classification
export type TempoClassification = 'much_faster' | 'faster' | 'similar' | 'slower' | 'much_slower';

export interface ErrorIndicator {
  region_id: string;
  region_name: string;
  frame_number: number;
  error_score: number;
  error_type: string;
  severity: 'low' | 'medium' | 'high';
  description: string;
}

export interface RegionScore {
  region_id: string;
  region_name: string;
  score: number;
  error_frames: number[];
  average_deviation: number;
  max_deviation: number;
}

/**
 * Per-bone or per-joint score detail including trajectory modulation factor.
 * Returned in PhaseScore.component_details when trajectory data is available.
 */
export interface ComponentDetail {
  /** Human-readable label, e.g. "Left Arm", "Left Knee" */
  label: string;
  /** Score type: bone direction similarity or joint angle similarity */
  type: 'bds' | 'jas';
  /** Average score for this component, 0-100 */
  avg_score: number;
  /**
   * How well the movement PATH matched the template (0-1).
   * 1.0 = perfect trajectory match; lower means the body part moved
   * via a different path even if the final pose was similar.
   * null when trajectory data is unavailable (static phase, etc.).
   */
  trajectory_factor: number | null;
}

// B-2026-0111 (Phase 0) — canonical speed_assessment contract (mirrors
// backend/app/services/speed_assessment.py build_speed_assessment()).
// Emitted as metadata.speed_assessment: SpeedAssessment[] ONLY when the template
// opts in (metadata.speed_mode === 'on'); absent otherwise. Diagnostic + coaching
// only in Phase 0 (no score impact).
export type SpeedDirection = 'match' | 'reward_faster' | 'reward_slower' | 'off';
export type SpeedVerdict = 'on_target' | 'too_fast' | 'too_slow' | 'not_evaluated';
export type SpeedMagnitude = 'slight' | 'notable' | 'significant';

// Old -> new vocabulary alias. Kept for backward compat: templates authored before
// P1-3a still carry old literals (faster_ok/slower_ok/any) in their stored data.
// SPEED_DIRECTION_ALIAS normalizes either vocab to the canonical SpeedDirection.
export const SPEED_DIRECTION_ALIAS: Record<string, SpeedDirection> = {
  match: 'match',
  faster_ok: 'reward_faster',
  slower_ok: 'reward_slower',
  any: 'off',
  reward_faster: 'reward_faster',
  reward_slower: 'reward_slower',
  off: 'off',
};

export interface SpeedAssessment {
  phase_label: string;
  direction: SpeedDirection;
  duration_ratio: number | null;        // fps-normalized; null when not_evaluated
  template_duration_ms: number | null;
  athlete_duration_ms: number | null;
  delta_ms: number | null;
  delta_pct: number | null;
  verdict: SpeedVerdict;
  magnitude: SpeedMagnitude | null;
  phase_weight: number;
  regime: string;                        // provenance (segmental_dtw | dtw_global | velocity_or_fallback)
  confidence: number | null;             // segmental alignment_score 0-100
  fps_source: 'measured' | 'defaulted';
}

export interface PhaseScore {
  phase_id?: string;
  phase_name?: string;
  phase_label?: string;
  phase_index?: number;
  start_frame?: number;
  end_frame?: number;
  duration_frames?: number;
  score?: number;
  spatial_score?: number;
  temporal_score?: number;
  overall_phase_score?: number;
  timing_score?: number;
  form_score?: number;
  timing_ratio?: number;
  template_duration_ms?: number;
  athlete_duration_ms?: number;
  template_duration_frames?: number;
  athlete_duration_frames?: number;
  feedback?: string[];
  region_scores?: RegionPhaseScore[];
  key_errors?: ErrorIndicator[];
  /** Per-bone/per-joint trajectory-modulated score breakdown */
  component_details?: ComponentDetail[];
  /**
   * Phase contribution weight from phase importance setting.
   * Populated by the phase-weighted scoring pipeline.
   * Low-importance phases get weight ~0; critical phases get weight > 1.
   * Defaults to 1.0 when absent (treat all phases equally).
   */
  phase_weight?: number | null;
}

export interface FrameScore {
  frame_index: number;
  score: number;
  is_error: boolean;
  procrustes_disparity?: number;
  phase_id?: string;
  phase_label?: string;
}

export interface RegionPhaseScore {
  region_id: string;
  region_label: string;
  phase_id: string;
  phase_label: string;
  procrustes_score: number;
  angle_score: number;
  visibility_score: number;
  weighted_score: number;
  error_frame_count: number;
  total_frame_count: number;
}

export interface RegionHull {
  region_id: string;
  region_label: string;
  region_score: number;
  hulls: Record<string, number[][]>;
}

export interface LandmarkDeviation {
  frame_index: number;
  landmark_id: number;
  landmark_name: string;
  deviation_score: number;
  is_significant: boolean;
  // Coordinate fields for visualization overlays
  athlete_x?: number;
  athlete_y?: number;
  template_x?: number;
  template_y?: number;
  deviation_distance?: number;
}

// ============================================================================
// Economy of Motion Types
// ============================================================================

/** Per-landmark detail within a single phase's economy analysis */
export interface EconomyLandmarkDetail {
  /** Anatomical or functional landmark name */
  name: string;
  /** Economy score 0–100 (lower = more wasted motion) */
  score: number;
  /** Directional efficiency component 0–1 */
  direction: number;
  /** Magnitude efficiency component 0–1 */
  magnitude: number;
}

/** Economy of motion data for a single movement phase */
export interface EconomyPhaseData {
  /** Overall economy score for this phase, 0–100 */
  phase_score: number;
  /** Number of landmarks that contributed to this score */
  n_scored: number;
  /** Landmarks sorted worst-first for coaching relevance */
  worst_landmarks: EconomyLandmarkDetail[];
}

/** Kinetic chain issue detected across multiple landmarks */
export interface EomChainIssue {
  chain: string;
  display_name: string;
  landmarks: string[];
  pattern: 'direction_mismatch' | 'tempo_mismatch' | 'mixed';
  avg_score: number;
  worst_landmark: string;
  worst_score: number;
  insight: string;
}

/** Per-landmark narrative with issue classification and insight */
export interface EomWorstLandmark {
  name: string;
  score: number;
  issue_type: 'direction' | 'magnitude' | 'both' | 'parasitic' | 'none';
  direction_score: number;
  magnitude_score: number;
  velocity_ratio: number | null;
  insight: string;
}

/** EOM narrative data for a single phase */
export interface EomPhaseNarrative {
  phase_score: number;
  n_scored: number;
  n_issues: number;
  dominant_issue: 'direction' | 'magnitude' | 'parasitic' | 'mixed' | 'none';
  summary: string;
  chain_issues: EomChainIssue[];
  parasitic_landmarks: Array<{ name: string; insight: string }>;
  worst_landmarks: EomWorstLandmark[];
  speed_intent?: 'match' | 'reward_faster' | 'reward_slower' | 'off';
}

/** Complete EOM narrative for an evaluation */
export interface EomNarrative {
  headline: string;
  economy_pattern: string;
  overall_score: number | null;
  n_phases_with_issues: number;
  phases: Record<string, EomPhaseNarrative>;
}

// ============================================================================
// Error Analysis Types (Phase A - Structured Error Extraction)
// ============================================================================

export interface RegionError {
  region_id: string;
  region_label: string;
  score: number;
  severity: ErrorSeverity;
  procrustes_score: number;
  angle_score: number;
  visibility_score: number;
  primary_issue: 'positioning' | 'angles' | 'visibility';
  angle_deviations?: Array<{
    joint_name: string;
    deviation: number;
    athlete_angle: number;
    template_angle: number;
  }>;
}

export interface TempoAnalysis {
  classification: TempoClassification;
  duration_ratio: number;
  percent_difference: number;
  severity: ErrorSeverity;
  phase_timing?: Record<string, number>;
}

export interface KeyIssue {
  issue_type: 'region_form' | 'region_positioning' | 'tempo' | 'visibility';
  region_id?: string;
  region_label?: string;
  severity: ErrorSeverity;
  score: number;
  description: string;
}

export interface ErrorFrameRange {
  start_frame: number;
  end_frame: number;
  frame_count: number;
  avg_score: number;
}

export interface ErrorAnalysisResult {
  overall_score: number;
  overall_severity: ErrorSeverity;
  region_errors: RegionError[];
  tempo_analysis?: TempoAnalysis;
  key_issues: KeyIssue[];
  error_frame_ranges?: ErrorFrameRange[];
  num_regions_analyzed: number;
  num_significant_errors: number;
}

// ============================================================================
// Grade-feel scoring (BD-7 Layer G) — track C consumes track B's contract
// ============================================================================

/**
 * Audit trail for whether/why the per-template grade scale (Layer G) was
 * applied to derive `overall_score` from `metadata.raw_match_score`.
 *
 * Contract: `backlog/grade-feel-scoring-plan.md` v3 addendum, "Evaluation
 * metadata" section of the grade-scale build contract. Shape is
 * single-authored there — do not add fields here that aren't in that
 * contract; treat any field this app doesn't recognize as forward-compatible
 * noise, not a shape to imitate.
 *
 * Absent on legacy evaluations (pre-dates this field) — callers MUST treat
 * absence the same as `active: false` (render nothing new).
 */
export interface GradeScaleApplied {
  /** True when overall_score = apply_grade_scale(raw_match_score, block); false = identity (overall_score === raw_match_score). */
  active: boolean;
  /** Why the scale did/didn't apply. Null only when active=true (no reason needed). */
  reason: 'no_block' | 'refused' | 'fingerprint_mismatch' | 'gate_zero' | null;
  /** Number of anchors in the fitted scale (0 when no block exists). */
  anchor_count: number;
  /** ISO timestamp copied from the calibration block's computed_at. */
  computed_at: string;
}

// ============================================================================
// LLM Explanation Types (Phase B - OpenAI-Generated Feedback)
// ============================================================================

export interface RegionCallout {
  region_label: string;
  callout_text: string;
}

export interface ScoreDecompositionItem {
  area: string;
  points_lost: number;
  measured_value?: string | null;
  expected_value?: string | null;
  explanation: string;
}

/** A single improvement tip with an optional video timestamp anchor. */
export interface ImprovementTip {
  /** Coaching tip text (1-3 sentences). */
  text: string;
  /**
   * When this tip is grounded in a holistic_trajectory_observation, this is
   * that observation's worst_frame_pct (0-100). Clicking the chip in the UI
   * calls seekToFrame to scrub the video player to that moment.
   * Null when the tip is not tied to a specific frame.
   */
  source_frame_pct?: number | null;
}

export interface LLMExplanation {
  score_decomposition?: ScoreDecompositionItem[];
  overall_summary: string;
  strengths?: string[];
  improvement_tips: string[];
  /**
   * Parallel list to improvement_tips. Each entry carries the tip text plus
   * an optional source_frame_pct for the video timestamp chip.
   * Present only when the LLM grounded at least one tip in a trajectory
   * observation. Null/absent on older evaluations.
   */
  improvement_tip_anchors?: ImprovementTip[] | null;
  region_callouts?: RegionCallout[];
  tempo_feedback?: string;
  next_steps?: string[];
  generated_at: string;
  llm_model: string;
  tokens_used?: number;
  /** Gemini Vision observations attached to this explanation, if available */
  gemini_observations?: GeminiErrorObservation[] | null;
}

// ============================================================================
// Tracked Landmarks Type
// ============================================================================

// ============================================================================
// Evaluation Result Type
// ============================================================================

export interface EvaluationResult {
  evaluation_id: string;
  template_id: string;
  /** Human-readable template name, added via TemplateIndex outer-join on the backend.
   *  Null when the template has been deleted or the join finds no match. */
  template_name?: string | null;
  template_video_id?: string;
  athlete_video_id: string;
  overall_score?: number;
  overall_procrustes_score?: number;
  overall_timing_score?: number;
  phase_scores?: PhaseScore[];
  region_scores?: RegionScore[];
  error_indicators?: ErrorIndicator[];
  ai_explanation?: string;
  improvement_tips?: string[];
  created_at?: string;
  frame_scores?: FrameScore[];
  athlete_region_hulls?: RegionHull[];
  template_region_hulls?: RegionHull[];
  landmark_deviations?: LandmarkDeviation[];
  error_threshold?: number;
  llm_feedback?: string;
  /**
   * `null` until the evaluation job reaches `completed` — the backend sends
   * `null`, not an absent key (`backend/app/models/evaluation.py`:
   * `llm_explanation: Optional[Any] = Field(default=None)`), and production
   * code branches on exactly that (`hooks/useEvaluation.ts` waits for
   * `data.llm_explanation != null`).
   *
   * The `| null` was missing until FE-15 typechecked the test files and four
   * fixtures named "when llm_explanation is null" stopped compiling. `!= null`
   * happens to be true for `undefined` too, which is why the wrong type went
   * unnoticed.
   */
  llm_explanation?: LLMExplanation | null;
  error_analysis?: ErrorAnalysisResult;
  phase_mapping?: PhaseMapping | null;
  // Map of annotation_id -> result. Keys follow these suffixes by goal type:
  // - (no suffix)   → spatial distance goal  (SpatialGoalResult)
  // - _movement     → trajectory/movement goal (SpatialGoalResult with trajectory_result)
  // - _axis         → axis alignment goal     (AxisGoalResult)
  // - _timing       → timing coordination goal (TimingGoalResult)
  // - _joint_angle  → joint angle goal        (SpatialGoalResult-like)
  spatial_goal_results?: Record<string, SpatialGoalResult | AxisGoalResult | TimingGoalResult>;
  // --- Goal Score (PR-2..PR-6, docs/goal_score/) -------------------------
  // All five fields are null/[] whenever SPORTFORM_GOAL_SCORE is off
  // (default) or no eligible Goal measured. overall_score (Form) is NEVER
  // touched by any of these. See types/goalScore.ts and
  // docs/API_BACKEND.md § "Goal Score".
  /** Sum of per-Goal bonuses for exceeding a target. `null`, never `0.0`, when nothing eligible measured (LFT-063). */
  goal_bonus_score?: number | null;
  /** The template's Form-effect curve, evaluated at this evaluation's own Form score. `null` together with `total_score` whenever `goal_bonus_score` is `null` (no Total=Form fallback). */
  form_effect?: number | null;
  /** First 12 hex chars of sha256 over the curve actually used — detects a mid-series curve edit. */
  form_effect_curve_hash?: string | null;
  /** `goal_bonus_score * form_effect`. `null` iff `goal_bonus_score` is `null`. */
  total_score?: number | null;
  /** Per-Goal-eligible-checkpoint projection, including abstained ones (visible with `bonus: null`). */
  goals?: GoalScoreItem[];

  // Gate failure information (automatic fail conditions)
  automatic_fail?: boolean;
  gate_failures?: Array<{
    goal_type: string;
    goal_key: string;
    annotation_label: string;
    explanation: string;
  }>;
  metadata?: {
    total_frames: number;
    duration_seconds: number;
    alignment_quality?: number;
    /**
     * Phase-importance-weighted timing score across all phases (0-100 range).
     * Set by the backend scoring pipeline. Used as the primary timing score source
     * in EvaluationResults.tsx before falling back to per-phase averages.
     */
    overall_timing_score?: number | null;
    /**
     * Audit trail for how the mirroring / handedness decision was made.
     * source values:
     *   "layer1_declared"  — lead side was declared (template + profile or override)
     *   "symmetric_skip"   — template is symmetric; mirroring skipped entirely
     *   "layer2_detection" — declared source unavailable; lead side inferred from video
     */
    mirroring_decision?: {
      source: 'layer1_declared' | 'layer1_5_athlete_override_detected_template' | 'layer1_6_template_declared_athlete_detected' | 'symmetric_skip' | 'layer2_detection' | 'rotational_invariant' | 'layer3_diagnostic';
      should_mirror: boolean;
      template_lead_side?: string | null;
      athlete_lead_side?: string | null;
      override_used?: boolean;
      /** Delta score (positive = mirroring would score higher) present when source === 'layer3_diagnostic' */
      diagnostic_pair_delta?: number | null;
    };
    // 3D lifting fields
    '3d_lifting_enabled'?: boolean;
    '3d_model'?: string | null;
    '3d_joint_count'?: number;
    '3d_gravity_aligned'?: boolean | null;
    // Gemini Vision fields
    gemini_observations?: GeminiErrorObservation[] | null;
    gemini_model?: string;
    gemini_frame_count?: number;
    gemini_processing_time_ms?: number;
    gemini_error?: string;
    // Multi-person selection
    focal_person_index?: number;
    person_count?: number;
    // Global Body Match / body form score averaged across phases (0-100 when present)
    global_body_score?: number | null;
    // Movement Economy — per-phase economy of motion scores
    economy_of_motion?: Record<string, EconomyPhaseData>;
    // EOM Narrative — structured coaching narrative with chain grouping and insights
    eom_narrative?: EomNarrative;
    // Trail enabled by default — true when the template is configured to auto-enable
    // trajectory trails during evaluation playback. Absent when not set.
    trail_enabled_by_default?: boolean;
    // Holistic trajectory observations — multi-landmark body path comparisons from the
    // holistic_trajectory module. Each entry describes a full-body movement pattern
    // (e.g., scooping, trajectory divergence) detected during the evaluation.
    holistic_trajectory_observations?: unknown[];
    // Holistic trajectory regions — per-region trajectory analysis summaries
    // produced alongside holistic_trajectory_observations.
    holistic_trajectory_regions?: unknown[];
    /**
     * PTD-10: position label for positional team-drill child evals (e.g. "PG", "SG").
     * Absent (null) for solo and squad-mode evals.
     */
    focal_figure_label?: string | null;
    /**
     * B-2026-0089: partial-body coverage. Present when one or more body regions
     * were off-frame (median visibility below the occlusion gate) in the athlete
     * video and therefore excluded from 3D, coaching, and scoring.
     * - excluded_regions: all athlete-off-frame regions (scored on what's visible).
     * - accidental_regions: excluded regions the TEMPLATE still shows — the athlete
     *   likely cut them off by mistake. Drives the results warning banner.
     */
    partial_body?: {
      excluded_regions: string[];
      accidental_regions: string[];
      gate: number;
    };
    /**
     * B-2026-0114 (P0-4) — per-phase speed assessment.
     * Emitted only when the template opts in (speed_mode === 'on').
     * Absent / undefined when speed is off or no phases were evaluated.
     */
    speed_assessment?: SpeedAssessment[];
    /** Master speed switch: 'on' | 'off'. Absent for legacy templates (treat as 'off'). */
    speed_mode?: 'on' | 'off';
    /**
     * N4 (B-2026-0126) — deterministic movement-analysis observations.
     * Populated by the trace scorer when enabled. Absent on evals where the
     * trace scorer did not run. Each entry carries a pre-built
     * `deterministic_sentence` that is the ONLY text to render; internal
     * numeric fields (sigma, raw_dev, etc.) are intentionally excluded from
     * the UI contract.
     *
     * Typed inline here to avoid a circular import with MovementAnalysis.tsx.
     * The canonical interface is DeterministicObservation in
     * src/components/evaluation/MovementAnalysis.tsx.
     */
    deterministic_observations?: Array<{
      phase_name: string;
      physical_landmark_name: string;
      body_region: string;
      channel: 'path' | 'range';
      deterministic_sentence: string;
      magnitude_band: 'silent' | 'mild' | 'moderate' | 'strong' | 'implausible';
      is_positive: boolean;
      is_plausibility_flagged: boolean;
      // Internal numeric fields — MUST NOT be rendered. Mirrors the optional
      // fields on the canonical DeterministicObservation interface in
      // src/components/evaluation/MovementAnalysis.tsx (kept in sync manually
      // per that file's own comment, to avoid a circular import).
      raw_dev?: number;
      sigma?: number;
      sigma_bands?: number;
      ratio?: number;
      template_diameter?: number;
      athlete_diameter?: number;
    }> | null;
    // -------------------------------------------------------------------------
    // BD-7 / D10 — per-dimension pricing result (additive, optional)
    // Present only when pricing_enabled=True on the backend and the evaluation
    // ran through the pricing hook. Absent for all legacy evals and when the
    // feature flag is off.
    // -------------------------------------------------------------------------
    /**
     * Top-level verdict for the pricing run.
     * 'scoreable' — within all boundaries; score is unmodified.
     * 'severe'    — one or more dims fired Ks (soft boundary); score may be reduced.
     * '0-class'   — one or more dims fired Kc (hard boundary); overall_score set to 0.0.
     */
    /**
     * Gates transformation step 2/3: rotation-boundary graded ceiling audit.
     * Present when the eval carries a rotation-profile boundary verdict;
     * `capped` false (or score_before_cap === cap-adjusted overall) means the
     * breach existed but the natural score sat below the cap.
     */
    boundary_ceiling_applied?: {
      annotation_label?: string;
      profile_median?: number;
      boundary_threshold?: number;
      boundary_ratio: number;
      cap: number;
      score_before_cap: number;
      capped?: boolean;
      n_breaches?: number;
    };
    pricing_verdict?: 'scoreable' | 'severe' | '0-class';
    /**
     * Full per-dimension pricing result from the evaluation pipeline.
     * Shape matches the §3.3 contract in test_pricing_pipeline.py.
     */
    pricing_result?: {
      /** Same as pricing_verdict — top-level aggregate verdict. */
      verdict: 'scoreable' | 'severe' | '0-class';
      /** Dimension keys that fired a Kc boundary (causing the 0-class verdict). */
      dims_fired: string[];
      /** EXP-010 A7: graded ceiling record, present only when the backend flag is on. */
      zero_class_ceiling?: { enabled: boolean; applied: boolean; cap: number | null; r: number | null; dimension: string | null; effective_score?: number | null };
      /** Per-dimension measurement results, keyed by dimension label (e.g. "B_motion"). */
      per_dim_scores: Record<
        string,
        {
          /** Z-score relative to the calibration envelope. Null if not computable. */
          z: number | null;
          /** Pricing score for this dimension (0-100). Null if not computable. */
          score: number | null;
          /** Per-dimension verdict. */
          verdict: 'scoreable' | 'severe' | '0-class';
          /** Hard boundary (Kc). Null if not minted for this template. */
          Kc: number | null;
          /** Soft boundary (Ks). Null if not minted for this template. */
          Ks: number | null;
        }
      >;
      /** Aggregate pricing score (0-100). 0.0 when is_zero_class is true. */
      aggregate_score: number;
      /** True when any dim fired Kc, triggering overall_score = 0.0 (Ruling 4). */
      is_zero_class: boolean;
      /** Verification status of the template at eval time. */
      template_verification_status?: string;
    };
    // -------------------------------------------------------------------------
    // BD-7 grade-feel scoring (track B, story 6) — additive, optional.
    // Present only when track B's application site ran (evaluation_pipeline.py,
    // after the pricing hook, before finalize). Absent for all legacy evals and
    // whenever no per-template grade scale exists — treat absence exactly like
    // grade_scale_applied.active === false: render nothing new (rule #1: the
    // Overall number is unchanged/unaffected either way — it already IS the
    // graded number when active).
    // -------------------------------------------------------------------------
    /** The pre-grade measurement M (0-100, 2dp). Equals overall_score when grade_scale_applied.active is false. */
    raw_match_score?: number | null;
    /** Audit trail for the grade-scale application decision. See GradeScaleApplied for field docs. */
    grade_scale_applied?: GradeScaleApplied | null;
    /**
     * Checkpoint coverage for this evaluation (viewpoint Phase 1). Absent on
     * every evaluation produced before the contract — treat absence as
     * "full coverage, nothing to say" and render nothing.
     */
    coverage?: EvaluationCoverage | null;
    // Index signature — allows the backend to add new metadata fields without
    // requiring a TS type update for every small addition. Deliberately `any`
    // (not `unknown`): at least one existing call site
    // (src/hooks/usePoseExtraction.ts's extractLandmarks3DFromMetadata, an
    // unused/dead export — grepped, zero callers) reads an undeclared field
    // off this index signature and forwards it as a concrete typed value
    // without narrowing. Switching to `unknown` breaks that call site (and
    // potentially others across this ~150-file codebase not audited here);
    // fixing it is out of scope for this lint-only batch (that file isn't in
    // this batch). Flagged for a follow-up pass rather than fixed here.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentional wide catch-all for forward-compatible/undeclared backend metadata fields; `unknown` breaks an out-of-batch call site (see comment above)
    [key: string]: any;
  };
  // Mirroring/handedness information for visualization alignment
  mirrored?: boolean;
  template_lead_side?: 'left' | 'right' | 'unknown';
  athlete_lead_side?: 'left' | 'right' | 'unknown';
  /** Attribution: display name of the coach who triggered this evaluation.
   *  Null for self-evals. Only populated when the detail endpoint returns it
   *  (currently available via /users/me/evaluations; deferred for /evaluations/{id}). */
  created_by_display_name?: string | null;
  /**
   * ID of the user who submitted this evaluation.
   * Null for self-evals. Returned by GET /evaluations and used in
   * EvaluationsList to filter "Show only evals I submitted".
   */
  created_by_user_id?: string | null;
  /**
   * ID of the coach this evaluation was shared with at submit time.
   * Null when the evaluation was not shared. The coach gains read + comment
   * access and is notified via evaluation_shared_with_you on completion.
   */
  shared_with_user_id?: string | null;
  /**
   * Display name of the coach this evaluation was shared with.
   * Populated by the backend when shared_with_user_id is set.
   */
  shared_with_display_name?: string | null;
}

export interface EvaluationRequest {
  template_id: string;
  athlete_video_id: string;
  include_explanation?: boolean;
  focal_person_index?: number;
  /**
   * Override the athlete's declared lead side for this specific evaluation.
   * Null = use profile default. Only relevant for asymmetric templates.
   */
  lead_side_override?: 'left' | 'right' | null;
  /**
   * Share this evaluation with a coach (user UUID).
   * When set, the eval is owned by the submitter (athlete) and the specified
   * coach gains read + comment access. The coach is notified via
   * evaluation_shared_with_you when the evaluation completes.
   * Omit to keep the evaluation private to yourself.
   */
  shared_with_user_id?: string | null;
  /**
   * Coach-submits-for-athlete (Goal E): the athlete this evaluation is FOR,
   * as a user UUID. When set, the evaluation is owned by that athlete
   * (EvaluationIndex.user_id = athlete), not the submitting coach — the
   * coach keeps read/comment/delete access via created_by_user_id. The
   * athlete must answer a publish-consent prompt before it can appear on a
   * leaderboard. Mutually exclusive with shared_with_user_id — the backend
   * rejects a request that sets both with a 400. Omit for a normal
   * self-submitted evaluation.
   */
  athlete_user_id?: string | null;
  /**
   * First frame (0-based, inclusive) to include in scoring.
   * Null / absent → use the full video from frame 0.
   * When set, the worker physically trims the video before pose extraction so
   * all frame indices in the result are relative to the trimmed clip.
   */
  athlete_trim_start_frame?: number | null;
  /**
   * Last frame (0-based, inclusive) to include in scoring.
   * Null / absent → use the full video to the last frame.
   * Only meaningful when athlete_trim_start_frame is also set; the pair is
   * validated by the backend (end > start, clip >= MIN_SCOREABLE_FRAMES).
   */
  athlete_trim_end_frame?: number | null;
}

// ============================================================================
// Multi-Person Detection Types
// ============================================================================

export interface PersonInfo {
  person_index: number;
  /** Normalized [0-1] bounding box: [x1, y1, x2, y2] */
  bbox: [number, number, number, number];
  confidence: number;
  /** Base64-encoded JPEG crop of the detected person, as a data URL */
  crop_data_url?: string;
  /** Face detection confidence score (0–1). Present when the backend ran a face check. */
  face_score?: number;
  /** True if the person's face is visible in the sample frame. False when the face check failed.
   * Persons who never show a face are normally filtered out server-side; this field is present
   * as belt-and-suspenders for in-flight responses and UI display. */
  face_visible?: boolean;
  /**
   * Composite relevance score [0.0, 1.0] computed by the backend from face visibility,
   * bounding-box area, frame centrality, and detection confidence.
   * Used to sort candidates (highest first) and drive auto-selection.
   */
  rank_score: number;
  /**
   * True on AT MOST ONE candidate when it clearly dominates the others.
   * When true a "Recommended" badge is shown on that candidate's thumbnail.
   * False on every other candidate, or on all candidates when no one dominates.
   */
  recommended: boolean;
}

export interface VideoPersonsResponse {
  person_count: number;
  sample_frame_index: number;
  persons: PersonInfo[];
  /** URL to fetch the full sample frame image */
  sample_frame_url?: string;
  /** True when EVERY detected person failed the face-visibility check and the server returned
   * the full unfiltered list as an exemption. The UI should warn the user but not block them. */
  all_persons_faceless?: boolean;
}

export interface EvaluationResponse {
  message: string;
  evaluation: EvaluationResult;
}

// ============================================================================
// Spatial Goal Types
// ============================================================================

/** Pair of landmark names for normalization segments */
export interface SegmentPair {
  landmark_1: string;
  landmark_2: string;
}

/** Configuration for body-size normalization */
export interface NormalizationConfig {
  mode: 'auto' | 'segment_median';
  segments?: SegmentPair[];
  prefer_world_coords?: boolean;
}

/** Template-anchored target point using barycentric coordinates */
export interface TemplateAnchor {
  frame_ref: number;
  anchor_method: 'barycentric_triangle';
  triangle_landmarks: [string, string, string];
  barycentric_weights: [number, number, number];
  anchor_in_coords: 'image' | 'world';
}

/** Target for spatial goal - either a landmark or template anchor */
export interface SpatialTarget {
  type: 'landmark' | 'template_anchor';
  target_landmark?: string;
  template_anchor?: TemplateAnchor;
}

/** Tolerance configuration for spatial goal objectives */
export interface SpatialTolerance {
  kind: 'percent' | 'absolute';
  value: number;
}

/** Objective mode for spatial goal comparison */
export interface SpatialObjective {
  mode: 'match_template' | 'less_or_equal' | 'greater_or_equal' | 'equal' | 'minimize' | 'maximize';
  tolerance?: SpatialTolerance;
}

/** Complete spatial goal definition */
export interface SpatialGoal {
  enabled: boolean;
  source_landmark: string;
  target: SpatialTarget;
  objective?: SpatialObjective;
  normalization?: NormalizationConfig;
  sample_window_frames?: number;
  // Gate condition: if true, failing this goal = automatic 0 for evaluation
  is_gate?: boolean;
  // Note: trajectory moved to separate movement_goal field in Region/Annotation
}

// ============================================================================
// Checkpoint observability (viewpoint Phase 1 contract)
// ============================================================================

/**
 * Why a checkpoint produced no score. The evaluator withheld a number rather
 * than guessing one — the score field is `null`, NEVER 0.0 (the LFT-063
 * sentinel rule: a withheld measurement is not a measurement of zero).
 *
 * Each value maps to one coach-facing explanation; the mapping lives in
 * `@/utils/checkpointCoverageCopy` (the single source of that copy).
 */
export type GoalAbstentionReason =
  | 'insufficient_visibility'
  | 'insufficient_frames'
  | 'event_not_detected'
  | 'missing_template_reference'
  | 'not_applicable_view'
  | 'evaluation_error'
  /** Permanent configuration fault (PR-3, PHASE1_CONTRACT amendment): the check can never measure as set up. */
  | 'misconfigured'
  /** Goal Score PR-4: the phase's start/end could not be trusted well enough to time it (Speed goals only). */
  | 'low_boundary_confidence';

/**
 * Why a checkpoint's score is less precise than usual. Label-only — the score
 * itself is unchanged.
 */
export type GoalDegradeReason = 'far_side' | 'diagonal_view';

/** Per-checkpoint observability verdict. */
export type GoalEvaluationStatus = 'computed' | 'degraded' | 'abstained';

/**
 * Observability fields the backend adds to EVERY per-goal result dict
 * (`metadata.spatial_goal_results[<key>]`). All optional: evaluations produced
 * before this contract carry none of them, and `status` absent must be read as
 * "computed" by every consumer.
 */
export interface GoalObservabilityFields {
  /**
   * The TEMPLATE annotation id this goal came from (athlete-side annotation
   * ids stay uuid4 and change per evaluation, so they cannot identify a
   * checkpoint across evaluations — this can).
   */
  template_annotation_id?: string;
  status?: GoalEvaluationStatus;
  /** Set only when `status === 'abstained'`. */
  abstention_reason?: GoalAbstentionReason | null;
  /** Set only when `status === 'degraded'`. */
  degrade_reason?: GoalDegradeReason | null;
  /**
   * Plain-English name of the body part this checkpoint watches — "left
   * knee", "both heels". OPTIONAL: when present it is interpolated into the
   * visibility-reason copy ("The left knee was hidden for most of the
   * movement…"); when absent the copy falls back to a generic phrase.
   *
   * This is a DISPLAY string, never a landmark identifier — see
   * docs/COACH_FACING_COPY_STANDARD.md §2. The copy helper humanises any
   * stray snake_case defensively, but the backend is the right place to get
   * this right.
   */
  body_part?: string | null;
}

/** One withheld checkpoint, as listed by `metadata.coverage.abstained`. */
export interface AbstainedCheckpoint {
  template_annotation_id: string;
  /** The checkpoint name the coach typed. */
  label: string;
  goal_type: string;
  target_phase?: string | null;
  reason: GoalAbstentionReason;
  /**
   * Plain-English name of the body part this checkpoint watches — "left
   * knee", "both heels". Optional; see GoalObservabilityFields.body_part.
   */
  body_part?: string | null;
}

/**
 * How much of the coach's checkpoint set this video could actually be scored
 * against (`metadata.coverage`, one per evaluation).
 *
 * `configured` = checkpoints that ran (enabled); `assessed` = checkpoints that
 * produced a number. `configured - assessed` is the number withheld, and
 * `abstained` says which and why.
 */
export interface EvaluationCoverage {
  configured: number;
  assessed: number;
  degraded: number;
  abstained: AbstainedCheckpoint[];
}

/** Result of spatial goal evaluation */
export interface SpatialGoalResult extends GoalObservabilityFields {
  enabled: boolean;
  passed: boolean;
  raw_distance_template?: number;
  raw_distance_athlete?: number;
  normalized_distance_template?: number;
  normalized_distance_athlete?: number;
  scale_factor_template?: number;
  scale_factor_athlete?: number;
  delta_percent?: number;
  comparison_mode?: string;
  confidence: number;
  valid_frames?: number;
  total_frames?: number;
  explanation: string;
  skipped?: boolean;

  // NEW: Trajectory-specific results
  trajectory_result?: TrajectoryGoalResult;

  // ---------------------------------------------------------------------
  // Travel-distance target fields (TRAVEL_PR_A_CONTRACT.md §3). Present on
  // the `{annotation_id}_movement` result FLAT (same object as the fields
  // above) whenever `movement_goal.travel_target` was set on the goal — do
  // not read these off `trajectory_result` instead; the contract's source
  // of truth is the top-level, flat shape.
  // ---------------------------------------------------------------------

  /** Set on every movement_goal result (not travel-specific) — added here
   * because the contract lists it among the travel result fields and it
   * was previously undeclared on this type at all (scoring.py always sets
   * it via compute_movement_goal_overall_score, regardless of mode). */
  overall_score?: number | null;
  /** `"travel_target"` when this result came from a travel-distance target;
   * absent/other for every other movement_goal mode. */
  mode?: string;
  /** Mirrors `mode === "travel_target"` — the cheaper check to branch on. */
  travel_target_mode?: boolean;
  /** The athlete's measured ratio (4dp; UI should still show 2dp). */
  measured_travel_body_heights?: number | null;
  /** The template's own measured ratio, THIS evaluation (re-measured fresh,
   * never read back from the saved annotation). */
  demo_value_body_heights?: number | null;
  /** The RESOLVED target (contract §1.1). */
  target_body_heights?: number | null;
  /** The EFFECTIVE sigma used, after the enforced floor. */
  tolerance_body_heights?: number | null;
  /** True iff the floor raised a lower/absent coach-typed or declared-gap tolerance. */
  tolerance_floor_applied?: boolean;
  target_source_resolved?: TravelTargetSource;
  comparison?: TravelComparisonOperator;
  /** Athlete-side body-height definition actually used. Never render the raw
   * enum value to the coach — see travelDistanceCopy.ts. */
  body_height_definition_used?: BodyHeightDefinition | null;
  /** Template-side body-height definition actually used (always equal to
   * the athlete side when status="computed"). */
  template_body_height_definition_used?: BodyHeightDefinition | null;
  /** 0-1, fraction of the window the landmark was visible. */
  coverage?: number;
  /** Athlete-physical landmark name for display. */
  effective_source_landmark?: string | null;

  /** Coach-assigned label for this annotation (injected by scoring.py alongside goal results) */
  annotation_label?: string | null;
  /** Alias of `annotation_label` (E2E-21, PR-9): the same label, stamped with coverage's precedence so the two surfaces cannot disagree. */
  label?: string | null;
  /** Coach-assigned description for this annotation */
  annotation_description?: string | null;
  /** Coach-assigned notes for this annotation */
  annotation_notes?: string | null;
}

// ============================================================================
// Common API Response Types
// ============================================================================
export interface ApiError {
  detail: string;
  status_code?: number;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ============================================================================
// Bulk Delete Types
// ============================================================================
export interface BulkDeleteRequest {
  evaluation_ids: string[];
}

export interface BulkDeleteResponse {
  deleted_count: number;
  failed_count: number;
  deleted_ids: string[];
  failed_ids: string[];
  errors: Record<string, string>;
}

export interface DeleteAllResponse {
  deleted_count: number;
  message: string;
}

// ============================================================================
// Axis Goal Types (Body Axis Alignment)
// ============================================================================

export enum AxisReferenceType {
  VERTICAL = "vertical",
  HORIZONTAL = "horizontal",
  TEMPLATE_ANGLE = "template_angle",
  INITIAL_ANGLE = "initial_angle"
}

export interface AxisConfig {
  enabled: boolean;
  landmark_start: string;
  landmark_end: string;
  reference_type: AxisReferenceType;
  target_angle_degrees: number;
  angle_tolerance_degrees: number;
  /**
   * Who chose `angle_tolerance_degrees` (observability Phase 4, study metric
   * M8). 'coach' = the coach typed a number; 'default' = they reviewed it and
   * kept the standard setting; absent/null = legacy, never reviewed. Stored
   * verbatim; no evaluator reads it.
   */
  tolerance_source?: 'default' | 'coach' | null;
  measure_stability: boolean;
  stability_threshold?: number;
  // `max_angular_velocity` removed — it mirrored a backend field that no code
  // ever read. Stability is scored from `stability_threshold`.
  /** Optional barycentric anchor for the start point (overrides landmark_start position) */
  anchor_start?: TemplateAnchor;
  /** Optional barycentric anchor for the end point (overrides landmark_end position) */
  anchor_end?: TemplateAnchor;
  /** When true, measures whole-body rotation angle via 3D pose data instead of a landmark axis */
  use_rotation_angle?: boolean;
  /**
   * When true, enables the learned-profile gate for body rotation.
   * The pass/fail threshold is derived from good + fail calibration takes
   * (Kc boundary) rather than a fixed angle_tolerance_degrees value.
   * NEVER expose this field name or its value directly in the UI.
   * Coach-facing copy: "Disqualify if the athlete faces the wrong way."
   */
  rotation_profile?: boolean;
  /** When true, failing this goal zeros the overall score */
  is_gate?: boolean;
  /**
   * Goal Score PR-5 (the Angle carrier). "mean_deviation" (default) is
   * today's existing scoring, byte-identical, unchanged. "p95_max" is the
   * Angle Goal shape — the opt-in IS this field's value plus
   * `angle_goal_target` being set; there is no separate boolean flag.
   */
  aggregation?: 'mean_deviation' | 'p95_max';
  /** Only consulted when `aggregation === 'p95_max'`. `is_gate` and
   * `aggregation === 'p95_max'` can never both be set — a gate is
   * pass/fail, a Goal is graded (backend 422s the pairing). */
  angle_goal_comparison?: import('./trajectory').AngleGoalComparison | null;
  /** The bar to beat, degrees. Required when `aggregation === 'p95_max'`. */
  angle_goal_target?: import('./trajectory').AngleTarget | null;
  /**
   * PR-5. The angle the COACH typed as the failure line, in degrees, for the
   * rotation hard-limit shape (`use_rotation_angle` + `is_gate` +
   * `rotation_profile`) and only that shape.
   *
   * This is the number the scorer compares against — NOT
   * `angle_tolerance_degrees`, which it multiplies before comparing (plan
   * §C.3c's unit rule: a coach who typed 20 meaning "fail above 20°" used to
   * get a failure line at 60°). The two numbers answer two different
   * questions, so neither is derived from the other here and no coach-facing
   * string mixes them (see `utils/gateCopy.ts`).
   *
   * `null`/absent on a hard limit means no failure angle is set, and the
   * check grades rather than fails — never a placeholder number
   * (§C.3c "Neither → No gate ... Never a placeholder threshold").
   */
  coach_breach_degrees?: number | null;
  /**
   * N6e — Axis purpose archetype: 'teaching' | 'biomechanical' | null.
   * Coach-authored; never side-swapped or regex-transformed.
   */
  purpose_category?: 'teaching' | 'biomechanical' | null;
  /**
   * N6e — Coach-authored free text explaining WHY this axis matters.
   * Passed verbatim to the AI coaching narrative (N6d). Max ~300 chars.
   * Must NOT be run through any Left/Right label-mirroring transformation.
   */
  purpose_text?: string | null;
  // DELETED (PR-3, audit item FE-1): `verbal_target_priority`. It had no
  // backend `AxisConfig` counterpart, so Pydantic dropped it on every write —
  // a field that looked like a setting and changed nothing. It still exists
  // where it is real: on the EXTRACTOR's output
  // (`SuggestedGoal.verbal_target_priority`, types/teachingVideo.ts, which
  // mirrors `backend/app/models/teaching_video.py`). The materialiser decides
  // what to do with it; no client writes it into a goal config.
}

export interface AxisGoalResult extends GoalObservabilityFields {
  enabled: boolean;
  passed: boolean;
  mean_angle: number;
  std_dev_angle: number;
  max_deviation: number;
  template_mean_angle?: number;
  alignment_score: number;
  stability_score: number;
  /**
   * `null` when the evaluator ABSTAINED (`status === 'abstained'`) — never 0.
   * A true zero stays 0. Every consumer must render a withheld score as
   * "not scored", never as 0 (LFT-063 sentinel rule).
   */
  overall_score: number | null;
  confidence: number;
  valid_frames: number;
  total_frames: number;
  explanation: string;
  /** Per-frame axis angles (degrees) for valid frames */
  per_frame_angles?: number[];
  /** Per-frame absolute deviation from target angle (degrees) for valid frames */
  per_frame_deviations?: number[];
  /** Frame indices corresponding to per_frame_angles / per_frame_deviations entries */
  valid_frame_indices?: number[];
  /**
   * Effective landmarks actually evaluated — may differ from the annotation config
   * when handedness mirroring is applied (e.g. left_shoulder -> right_shoulder).
   */
  effective_landmark_start?: string;
  effective_landmark_end?: string;
  /**
   * Per-frame reconstructed anchor position for the start point (normalized 0-1 image coords).
   * Present only when anchor_start was configured. Entries may be null for frames where
   * the anchor triangle landmarks are not visible.
   */
  per_frame_anchor_start?: Array<[number, number] | null>;
  /**
   * Per-frame reconstructed anchor position for the end point (normalized 0-1 image coords).
   * Present only when anchor_end was configured. Entries may be null for frames where
   * the anchor triangle landmarks are not visible.
   */
  per_frame_anchor_end?: Array<[number, number] | null>;

  /** Coach-assigned label for this annotation (injected by scoring.py alongside goal results) */
  annotation_label?: string | null;
  /** Alias of `annotation_label` (E2E-21, PR-9): the same label, stamped with coverage's precedence so the two surfaces cannot disagree. */
  label?: string | null;
  /** Coach-assigned description for this annotation */
  annotation_description?: string | null;
  /** Coach-assigned notes for this annotation */
  annotation_notes?: string | null;
}

// ============================================================================
// Timing Goal Types (Inter-Landmark Timing Coordination)
// ============================================================================

export enum TimingMeasurementType {
  VELOCITY_ONSET = "velocity_onset",
  PEAK_VELOCITY = "peak_velocity",
  ANGLE_THRESHOLD = "angle_threshold",
  GROUND_CONTACT = "ground_contact",
  OFF_THE_GROUND = "off_the_ground",
  PEAK_EXTENSION = "peak_extension",
}

export enum TimingRelationship {
  SIMULTANEOUS = "simultaneous",
  SEQUENTIAL_A_FIRST = "sequential_a_first",
  SEQUENTIAL_B_FIRST = "sequential_b_first"
}

/**
 * Per-landmark event detection configuration.
 * Each landmark in a timing pair can use a different detection method.
 */
export interface LandmarkEventConfig {
  measurement_type: TimingMeasurementType;
  /** Velocity threshold for VELOCITY_ONSET (normalized units/frame). Default 0.02 */
  velocity_threshold?: number;
  /** Joint name for ANGLE_THRESHOLD (e.g., "left_knee"). Must match JOINT_DEFINITIONS. */
  angle_joint_name?: string;
  /** Target angle in degrees for ANGLE_THRESHOLD. Default 160 */
  angle_threshold_degrees?: number;
  /** "above" = extending past threshold, "below" = bending past threshold */
  angle_crossing_direction?: 'above' | 'below';
}

export interface TimingConfig {
  enabled: boolean;
  landmark_a: string;
  landmark_b: string;
  /** Per-landmark event config (takes precedence over legacy measurement_type) */
  event_a?: LandmarkEventConfig;
  event_b?: LandmarkEventConfig;
  /** @deprecated Use event_a/event_b instead. Kept for backward compatibility. */
  measurement_type: TimingMeasurementType;
  /** @deprecated Use event_a/event_b velocity_threshold instead. */
  velocity_threshold: number;
  relationship: TimingRelationship;
  simultaneity_tolerance_frames: number;
  min_lag_frames?: number;
  max_lag_frames?: number;
  use_template_reference: boolean;
  template_tolerance_percent: number;
}

export interface TimingGoalResult extends GoalObservabilityFields {
  enabled: boolean;
  passed: boolean;
  landmark_a_event_frame?: number;
  landmark_b_event_frame?: number;
  measured_lag_frames?: number;
  /**
   * Lag in milliseconds (fps-normalised). Populated by KS0.
   * Reliable for ordering (sign tells which landmark fired first); the absolute
   * value carries a structural onset→peak offset and is NOT the true physiological
   * inter-segment lag time.
   */
  measured_lag_ms?: number | null;
  template_lag_frames?: number;
  lag_deviation_frames?: number;
  /**
   * `null` when the evaluator ABSTAINED (`status === 'abstained'`) — never 0.
   * See AxisGoalResult.overall_score.
   */
  timing_score: number | null;
  confidence: number;
  explanation: string;
  /** Per-frame angle signal (for ANGLE_THRESHOLD events) */
  angle_signal_a?: (number | null)[];
  angle_signal_b?: (number | null)[];
  /** Per-frame Y-velocity signal (for GROUND_CONTACT events) */
  y_velocity_signal_a?: (number | null)[];
  y_velocity_signal_b?: (number | null)[];
  /** Per-frame velocity magnitude for landmark A */
  velocity_signal_a?: (number | null)[];
  /** Per-frame velocity magnitude for landmark B */
  velocity_signal_b?: (number | null)[];
  /** Whether measured lag was within min/max bounds */
  lag_within_bounds?: boolean;
  /**
   * Effective landmarks actually evaluated — may differ from the annotation config
   * when handedness mirroring is applied (e.g. left_wrist -> right_wrist).
   */
  effective_landmark_a?: string;
  effective_landmark_b?: string;
  /** Frame indices the goal was evaluated over (for scoping overlay visibility) */
  valid_frame_indices?: number[];

  /** Coach-assigned label for this annotation (injected by scoring.py alongside goal results) */
  annotation_label?: string | null;
  /** Alias of `annotation_label` (E2E-21, PR-9): the same label, stamped with coverage's precedence so the two surfaces cannot disagree. */
  label?: string | null;
  /** Coach-assigned description for this annotation */
  annotation_description?: string | null;
  /** Coach-assigned notes for this annotation */
  annotation_notes?: string | null;
}

// ============================================================================
// Joint Angle Goal Types
// ============================================================================

/**
 * How to determine the target angle for a joint angle goal.
 * Mirrors backend JointAngleReferenceType enum.
 */
export type JointAngleReferenceType = 'template' | 'target_range';

/**
 * Configuration for a joint angle goal annotation.
 * Mirrors backend JointAngleConfig (backend/app/models/spatial_goals.py:829).
 *
 * The single required field is `joint_name` — a key from JOINT_DEFINITIONS
 * (backend/app/services/joint_angle_goal_evaluation.py:34-48).
 * Functional names like "lead_knee" also resolve at eval time.
 *
 * For reference_type === "target_range", supply `target_angle_degrees` or
 * `min_angle_degrees` + `max_angle_degrees`.
 * For reference_type === "template", no explicit target is needed — the
 * evaluator derives the target from the reference video's own joint angles.
 */
export interface JointAngleConfig {
  enabled: boolean;
  /** Must be a key in JOINT_DEFINITIONS (e.g., "left_knee", "right_hip"). */
  joint_name: string;
  reference_type: JointAngleReferenceType;
  /** Explicit target angle (degrees, 0-180). Used in TARGET_RANGE mode. */
  target_angle_degrees?: number | null;
  /** Minimum acceptable angle (degrees). Alternative to target_angle_degrees. */
  min_angle_degrees?: number | null;
  /** Maximum acceptable angle (degrees). Alternative to target_angle_degrees. */
  max_angle_degrees?: number | null;
  /** Acceptable deviation from target (degrees). Default 15. */
  angle_tolerance_degrees: number;
  /** Who chose `angle_tolerance_degrees` — see AxisConfig.tolerance_source. */
  tolerance_source?: 'default' | 'coach' | null;
  /** When true, a stability score is also computed alongside alignment. */
  measure_stability: boolean;
  // DELETED (PR-3, audit item FE-16): `is_gate`. `backend/app/models/
  // spatial_goals.py:JointAngleConfig` has no such field, so the flag read
  // like working gate behaviour and did nothing. Gating is live on
  // `AxisConfig.is_gate` (with `rotation_profile`) and `SpatialGoal.is_gate`,
  // both of which the backend model really carries — see CLAUDE.md
  // "Adding a New Goal Evaluator" item 11 for the one carve-out.
  //
  // A joint-angle config also has no `target_min_degrees` /
  // `target_max_degrees` / `verbal_target_priority`, and never did (TV-3 /
  // FE-1 / E2E-3). The real range fields are `min_angle_degrees` /
  // `max_angle_degrees` above, with `reference_type: 'target_range'`.
  /**
   * Goal Score PR-5 (the Angle carrier) — same additive shape as
   * `AxisConfig`'s own three fields above. `JointAngleConfig` has NO
   * `is_gate` field at all (never did — see the comment block above), so
   * there is no gate/Goal overlap to guard against on this carrier.
   */
  aggregation?: 'mean_deviation' | 'p95_max';
  angle_goal_comparison?: import('./trajectory').AngleGoalComparison | null;
  angle_goal_target?: import('./trajectory').AngleTarget | null;
}

// ─── Credit distribution (D3, B-2026-0065) ────────────────────────────────────
// Mirrors backend/app/schemas/credit.py + admin credit-maintenance response.

/** Per-issuing-coach rollup inside a ChannelTokenBalance. */
export interface CoachAllocationSummary {
  granted: number;
  consumed: number;
  active_allocations: number;
}

/** Response from GET /channels/{slug}/token-balance (owner/admin). */
export interface ChannelTokenBalance {
  channel_id: string;
  available_credits: number;
  reserved_credits: number;
  active_granted: number;
  total_consumed: number;
  by_coach: Record<string, CoachAllocationSummary>;
}

/** A single credit allocation (authorisation grant). */
export interface CreditAllocation {
  allocation_id: string;
  issuing_coach_id: string;
  /** null = broadcast allocation (any member may draw). */
  recipient_user_id: string | null;
  credits_granted: number;
  credits_consumed: number;
  credits_remaining: number;
  /** 'active' | 'exhausted' | 'revoked' | 'expired'. */
  status: string;
  expires_at: string | null;
  created_at: string | null;
}

/** Body for POST /channels/{slug}/token-allocations. */
export interface CreateAllocationRequest {
  /** null = channel-broadcast allocation. */
  recipient_user_id?: string | null;
  credits_granted: number;
  expires_at?: string | null;
}

/** Response from DELETE /channels/{slug}/token-allocations/{id}. */
export interface AllocationRevokeResult {
  allocation_id: string;
  status: string;
  unspent_returned: number;
}

/** Body for PUT /channels/{slug}/coach-credit-caps/{coach_user_id}. */
export interface CoachCreditCapRequest {
  allocation_cap: number;
}

/** Response from the coach-credit-cap endpoints. */
export interface CoachCreditCap {
  channel_id: string;
  coach_user_id: string;
  allocation_cap: number;
  allocated_used: number;
  allocation_remaining: number;
  set_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Body for POST /channels/{slug}/token-grant (admin deposit). */
export interface TokenGrantRequest {
  credits: number;
  note?: string | null;
}

/** Response from POST /channels/{slug}/token-grant. */
export interface TokenGrantResult {
  channel_id: string;
  deposited: number;
  available_credits: number;
}

/** Response from POST /admin/credit-maintenance/run. */
export interface CreditMaintenanceResult {
  ledger_enabled: boolean;
  granted_channels: number;
  total_credits: number;
  expired_allocations: number;
  reaped_reserves: number;
  // Structured discrepancy rows are opaque here (admin diagnostics only).
  discrepancies: unknown[];
  discrepancy_count: number;
  ran_at: string;
}

// ============================================================================
// Suggestion materialisation (PR-3 of the coach-input refactor)
// ============================================================================
//
// ONE converter, and it lives on the backend. The frontend used to rebuild a
// goal config out of a teaching-video suggestion by hand
// (`utils/suggestedGoalToAnnotation.ts`, 632 lines) while the backend built a
// second, different one from the same data. The two drifted, and the drift was
// invisible: the client wrote `target_min_degrees` / `target_max_degrees` /
// `verbal_target_priority`, none of which exist on the backend's
// `JointAngleConfig`, so Pydantic dropped all three on every write and the
// angle range a coach said out loud never reached scoring (audit items TV-3,
// FE-1, E2E-3). The frontend is now a thin client: it posts the selected
// suggestions, and renders exactly what comes back.
//
// --- THE CONTRACT ----------------------------------------------------------
// POST /api/teaching-videos/{id}/materialize
//   request  : MaterializeRequest
//   response : MaterializationResult
//
// Both halves of this contract are built in parallel (this file and
// `backend/app/services/suggestion_materialization.py` +
// `backend/app/models/suggested_draft.py`). Everything the two sides must
// agree on is written down here so the two diffs can be compared key by key:
//
//  1. `source_ref` is the join key between a proposed item and its notice /
//     dropped entry, and it is ALWAYS present. Format: `"{kind}:{index}"`,
//     where `{index}` is the item's position in the matching
//     `TeachingVideoAnalysis` list and `{kind}` is one of
//     `suggested_goal` (`.suggested_goals`),
//     `body_part_emphasis` (`.body_part_emphasis`),
//     `coaching_phase` (`.phases`) or
//     `common_mistake` (`.common_mistakes`).
//     The frontend matches on the whole string; it never parses it. The last
//     two exist because the materialiser can refuse a phase or a mistake, and
//     the coach has to be told.
//  2. The backend owns EVERY coach-facing sentence in `notices[]`. The
//     frontend renders `label`, `what`, `why` and `how` verbatim and composes
//     nothing of its own (`docs/coach_input/REFACTOR_PLAN.md` §C.3d, §C.7 —
//     the catalogue is a committed YAML that the Python denylist test and this
//     type mirror).
//  3. A materialised annotation carries NO canvas coordinates. Canvas-less is
//     canonical (owner decision D5): the coach places the box afterwards, and
//     PR-4 adds the "suggest a box from the resolved body points" assist. The
//     old client invented a placeholder box at (0.4, 0.4, 0.2 x 0.2) for every
//     suggestion, which looked like a real position and was not one.
//  4. An item listed in `dropped[]` is NOT in `annotations[]`. Nothing is
//     appended silently and nothing is dropped silently (E2E-5 / E2E-6).
//  5. `materializer_version` is stamped on every annotation AND returned at
//     the top level (owner decision D10). Current value: **"3.0.0"**. A
//     suggestion compiled by an older materialiser is refused at accept time
//     with HTTP 409 and
//     `detail: { kind: "stale_suggestion", error: "stale_compiled_suggestion",
//     message, notice, stale_annotation_ids[], recompile_required: true,
//     expected_materializer_version }`. This client shows `detail.message`
//     (see `materializeSuggestions`), so the recompile prompt reaches the coach
//     in the backend's own words.
//  6. A focus-area region is RESOLVED or UNRESOLVED, never half of either
//     (PR-4, plan §C.3a). Resolved: `landmark_indices` + `landmark_names` +
//     `tracking: true` + `resolution`. Unresolved: none of those four, plus a
//     `notice`. `resolution` is the single "does this count?" signal — see
//     `RegionResolution`. A region's box is still never set by the backend
//     (note 3); `GET /api/templates/{id}/regions/{annotation_id}/placement`
//     derives one ON REQUEST, and returns `box: null` rather than invent one.

/**
 * Why a notice exists. Closed set, mirroring the backend notice catalogue
 * (`backend/app/config/coach_notices.yaml`).
 *
 * Nothing in the UI branches on this value — the coach-facing sentences all
 * arrive in `what` / `why` / `how`, and items are matched to cards by
 * `source_ref`. `kind` is for tests, telemetry and the catalogue-parity check,
 * so a value the frontend has not heard of still renders correctly.
 */
export type NoticeKind =
  // --- severity: 'dropped' - the check was NOT added ---
  /** The named joint is not one we can measure an angle at. */
  | 'unknown_joint'
  /** A joint-angle check was asked for without naming the joint. */
  | 'missing_joint'
  /** The named body point is not one the scorer can read. */
  | 'unscorable_body_part'
  /** The body part named is not one we recognise at all. */
  | 'unknown_body_part'
  /** The kind of check the coach described does not exist yet. */
  | 'unsupported_check_type'
  /** A line check needs two points and only one was named (E2E-5). */
  | 'incomplete_line'
  /** An order check needs two things to put in order; one was named. */
  | 'incomplete_order_check'
  /** Two things were named but not which comes first. */
  | 'order_not_clear'
  /** The two parts were described as moving at the same moment, which is not an order. */
  | 'same_moment_both_parts'
  /** The angle stated is outside what a joint can do. */
  | 'angle_out_of_range'
  /** Which side leads the movement is undeclared, so lead/trail cannot resolve. */
  | 'side_not_declared'
  /**
   * The movement was declared as working both sides the same way, so there is
   * no one side to pin a focus area to (PR-4, plan §C.3a row 3).
   */
  | 'symmetric_region_deferred'
  /**
   * The movement was declared as turning right through, so the side a focus
   * area would be pinned to changes during it (PR-4, plan §C.3a row 4).
   */
  | 'rotational_region_deferred'
  /** The suggestion was produced by an older version and must be redone (D10). */
  | 'stale_suggestion'
  // --- severity: 'degraded' - added, with something the coach should know ---
  /** Added, but scoped to the whole video because its phase name did not match. */
  | 'phase_not_matched'
  /** Two or more emphases on the same body part were merged into one. */
  | 'duplicate_focus_area'
  /** Added with the standard strictness because no number was stated. */
  | 'standard_strictness_used'
  /** Added with the standard weighting because none was stated. */
  | 'importance_set_to_standard'
  /**
   * Added as a hard limit, but no failure angle is set, so it grades instead
   * of failing (PR-5, plan §C.3c — "Never a placeholder threshold").
   */
  | 'gate_needs_a_number'
  /**
   * The coach said "never"/"must", and this kind of check cannot rule an
   * attempt out at all — so it was added as one of the most important checks
   * and the downgrade is stated on its face (PR-5, §C.3c shape (iii)).
   */
  | 'gate_not_possible_for_this_check'
  // --- severity: 'info' - no action implied ---
  /** Which number decides a failure on a hard limit (PR-5). */
  | 'gate_threshold_source'
  /**
   * The number the coach SAID and the number their DEMO showed disagree, and
   * the number they said is the one in force (PR-7, plan §C.3b(3)).
   *
   * The ONE kind the UI branches on — see `VerbalTarget` and
   * `VERBAL_TARGET_CONFLICT_KIND` in `utils/verbalTargetCopy.ts` for why.
   */
  | 'verbal_target_conflict'
  /** Added across the whole movement rather than one phase, on purpose. */
  | 'applies_throughout'
  /** The number the coach said out loud is the number the check uses. */
  | 'stated_number_used'
  /** Not turned into a check; kept as a coaching note instead. */
  | 'mistake_kept_as_coaching_note'
  // --- PR-8: what a check can and cannot be measured on ------------------
  /**
   * The check cannot be measured on this demo because of the angle it was
   * filmed from (PR-8; audit items `RC-4`, `TV-5b`).
   */
  | 'cannot_measure_camera_angle'
  /**
   * The check cannot be measured because the demo gives us nothing to compare
   * the athlete against (PR-8; audit item `RC-5`).
   */
  | 'cannot_measure_no_reference'
  /**
   * We cannot tell whether the check can be measured, because nobody has said
   * how the demo was filmed (PR-8 — `camera_view` is `None`, which is never
   * reported as "can measure").
   */
  | 'cannot_assess_camera_unknown'
  | 'candidate_not_agreed'
  /** This check is already on the template, so it was not added twice (PR-8, `TV-21`). */
  | 'duplicate_check'
  /** The check cannot be judged until the coach says which side leads (PR-8). */
  | 'declaration_required'
  /**
   * Only part of what the coach said became a check, so they are asked to
   * confirm the measurement before it counts (PR-8, audit item `RC-19`).
   */
  | 'partial_compile_confirm';

/**
 * What a notice means for the check it is about.
 *
 * - `dropped`  — the check was not added. Coach label: "Not added".
 * - `degraded` — the check was added, with something the coach should know.
 *                Coach label: "Added with a note".
 * - `info`     — nothing happened to the check; context only.
 */
export type NoticeSeverity = 'dropped' | 'degraded' | 'info';

/**
 * One coach-facing notice. The four text fields are the what / why / how of
 * `docs/COACH_FACING_COPY_STANDARD.md` §1 and are rendered VERBATIM — the
 * frontend must not rewrite, truncate, or assemble them.
 */
export interface Notice {
  kind: NoticeKind;
  severity: NoticeSeverity;
  /** Short heading, in the coach's own words. Usually names the check. */
  label: string;
  /** What happened (or what to do). One sentence. */
  what: string;
  /** Why, in one plain sentence a coach would say to another coach. */
  why: string;
  /** A concrete instruction, or the words for "nothing to do". */
  how: string;
  /** Join key back to the proposed item — see contract note 1 above. */
  source_ref: string;
}

/**
 * A proposed item that was NOT turned into a check.
 *
 * Carries enough to render on its own — a coach reading a list of these should
 * not have to go and find the card it came from.
 */
export interface DroppedItem {
  /** Join key back to the proposed item — always present (contract note 1). */
  source_ref: string;
  /** What kind of thing was dropped: a check, a focus area, a phase, a mistake. */
  kind?: string;
  /** The coach's own label for the item. */
  label?: string;
  /** The coach's own words that produced it, when the extractor captured them. */
  coach_quote?: string | null;
  /** Which notice explains this drop; the notice is also findable by `source_ref`. */
  notice_kind?: NoticeKind;
}

/** Counts for the one-line summary after a materialisation run. */
export interface MaterializationSummary {
  /** How many items the coach asked us to turn into checks. */
  proposed: number;
  /** How many became checks. */
  materialised: number;
  /** How many did not. */
  dropped: number;
  /**
   * How many NOTES were attached, not how many checks carry one — the backend
   * counts (check x shortfall) pairs, so one check with two shortfalls counts
   * twice. Any copy built from this says "notes", never "checks".
   */
  degraded: number;
}

/**
 * Proof that a focus-area region's body points were resolved to one side of
 * the body, and therefore that the region COUNTS in the score (PR-4).
 *
 * --- THE PR-4 CONTRACT, both halves ---------------------------------------
 * A focus-area region is in exactly one of two states, and the four fields
 * below move together. The backend's `resolve_region_indices` (plan §C.3a)
 * either resolves all of them or none of them — "fail closed" means it never
 * half-resolves:
 *
 *   RESOLVED    `landmark_indices` + `landmark_names` (anatomical) +
 *               `tracking: true` + `resolution`.
 *   UNRESOLVED  none of those four, plus a `notice` whose kind is one of
 *               `side_not_declared`, `symmetric_region_deferred`,
 *               `rotational_region_deferred`.
 *
 * The frontend therefore reads `resolution` as the single "does this count?"
 * signal and never infers one from a subset (an inference would be a second,
 * independent decision about the same thing — the bug class CLAUDE.md's
 * mirroring section bans). `tracking: true` WITHOUT `resolution` stays a valid
 * state: that is a region the coach drew on the canvas, whose indices came
 * from the drawn box rather than from a declared side.
 *
 * Nothing in here is rendered as-is. `side` becomes the coach's own words
 * ("on the right side"); `symmetry`, `materializer_version` and `resolved_at`
 * are provenance for support and tests, never coach-facing copy
 * (docs/COACH_FACING_COPY_STANDARD.md §2).
 */
export interface RegionResolution {
  /** Which physical side the region's body points landed on. */
  side: 'left' | 'right';
  /**
   * The template's declared movement symmetry at resolve time, as the backend
   * spells it (`asymmetric` today — the other values fail closed instead).
   * Kept as a plain string so a new value cannot break rendering.
   */
  symmetry: string;
  /** Which materialiser resolved it (D10 version stamp). */
  materializer_version: string;
  /** ISO-8601 timestamp of the resolve. */
  resolved_at: string;
}

/**
 * Where a resolved region's body points actually sit in the reference video,
 * so the coach can drop a box onto the canvas in one click instead of drawing
 * one from scratch.
 *
 * `GET /api/templates/{id}/regions/{annotation_id}/placement`
 *
 * `box` coordinates are NORMALISED 0-1 against the canvas, so the client
 * multiplies by the video's pixel dimensions. `box` is `null` whenever the
 * region is unresolved or its points were not readable in the reference frame
 * — and a `null` box is the one case the client must NOT paper over: owner
 * decision D5 drops the placeholder box precisely because a made-up position
 * looks like a chosen one.
 */
export interface RegionPlacementBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RegionPlacement {
  box: RegionPlacementBox | null;
  /** Which reference-video frame the box was derived from, if any. */
  frame_index: number | null;
  /** How the box was derived. One value today. */
  source: 'reference_frame_landmarks';
}

/**
 * Which number decides a failure on a hard limit (PR-5, plan §C.3c's three
 * threshold sources).
 *
 * - `coach`       — the coach typed the failure angle (`coach_breach_degrees`).
 * - `calibration` — a boundary was worked out from the coach's own clips.
 * - `none`        — neither, so the check grades and never fails.
 *
 * Resolved by the backend. The frontend reads it and never derives it from a
 * goal config: two independent answers to "whose number is this?" is the bug
 * class this refactor removes.
 */
export type GateThresholdSource = 'coach' | 'calibration' | 'none';

/**
 * What a check does when the athlete is off — the backend's own verdict,
 * stamped on a materialised or accepted annotation (PR-5).
 *
 * `shape` is the scorer's behaviour, which depends on the check's shape
 * (plan §C.3c):
 *   - `ceiling` — the score is capped from `breach_degrees` onwards (the
 *                 rotation hard limit: `is_gate` + `rotation_profile`).
 *   - `zero`    — the attempt is ruled out. This shape has no boundary object
 *                 of its own, so `breach_degrees` is `null` by construction.
 *   - `none`    — not a hard limit; the check marks the athlete down only.
 *
 * `breach_degrees` is the angle the scorer actually compares against, already
 * in the units the coach typed — the frontend does no arithmetic on it.
 *
 * Rendered through `utils/gateCopy.ts`; nothing in here is coach-facing as-is.
 */
export interface GateSummary {
  shape: 'ceiling' | 'zero' | 'none';
  threshold_source: GateThresholdSource;
  breach_degrees: number | null;
}

/**
 * An annotation the backend built by constructing the real Pydantic goal model
 * and serialising it with `model_dump(mode="json", exclude_none=True)`
 * (§C.3e). Ready to save as-is.
 *
 * --- The canvas keys the backend mirrors (§C.3e, enumerated from the
 * `Annotation` interface in `components/canvas/RegionAnnotator.tsx`) ---
 *   id, type, coordinates, importance, tracking, description, color, frame,
 *   timestamp, activeFrames, landmark_indices, landmark_names,
 *   functional_region, notes, target_phase
 * plus the five goal keys that type carries:
 *   spatial_goal, movement_goal, timing_goal, axis_goal, joint_angle_goal
 * and the two it does NOT carry today (a drift worth its own note):
 *   signed_projection_goal, height_comparison_goal
 * plus the backend-written keys:
 *   provenance, source_take_id, goal_type, critique_sentiment, label,
 *   materializer_version, authored_landmark_names, purpose_text, gate_summary
 *
 * `coordinates` is absent by contract (note 3). Every other canvas key is
 * optional here because the materialiser sets only what it actually knows —
 * the serialisation contract forbids injecting defaults.
 */
/**
 * The two numbers behind one check: the one the coach SAID out loud, and the
 * one their own demo actually showed (PR-7 of the coach-input refactor,
 * `docs/coach_input/REFACTOR_PLAN.md` §C.3b).
 *
 * The 2026-05-15 locked decision §3: **what the coach said beats what their
 * body did.** A coach who says "lead elbow straight, 170 degrees" is stating a
 * target, not describing their own demo — and their demo, measured, came out at
 * 163°. Before PR-7 the stated number never reached scoring at all (the client
 * wrote field names the backend model does not have, audit items TV-3 / FE-1 /
 * E2E-3), so every athlete was silently judged against the 163° the coach
 * happened to produce on camera.
 *
 * Present on a materialised goal annotation when the coach stated a target.
 * Absent means no number was said, so there is nothing to compare and nothing
 * to show.
 *
 * WHO DECIDES WHAT:
 *  · The BACKEND measures `demonstrated_value`, decides `conflict`, and
 *    resolves `priority`. The frontend never computes any of the three — it
 *    reads them and says which number is in force, in the coach's words
 *    (`utils/verbalTargetCopy.ts`).
 *  · `conflict: true` also produces a `verbal_target_conflict` notice whose
 *    what / why / how are rendered VERBATIM (contract note 2).
 *  · The coach switches by re-materialising with
 *    `MaterializeRequest.verbal_target_priority`.
 *
 * `demonstrated_value` is `null` when the template's own value could not be
 * measured for this check. Everything that compares the two numbers must treat
 * that as "nothing to compare" and render nothing — never as agreement, and
 * never as a conflict (the badge renders only when a measured value exists,
 * plan §C.3b(3)).
 */
export interface VerbalTarget {
  /** Low end of the range the coach stated, in degrees. */
  stated_min: number | null;
  /** High end of the range the coach stated. Equal to `stated_min` for a single number. */
  stated_max: number | null;
  stated_unit: 'degrees';
  /** What the coach's own demo measured for this check, or `null` if unmeasurable. */
  demonstrated_value: number | null;
  demonstrated_unit: 'degrees' | null;
  /** Which number the check is scored against, as the backend resolved it. */
  priority: 'verbal' | 'demonstrated';
  /** The backend's verdict that the two numbers disagree enough to tell the coach. */
  conflict: boolean;
}

/**
 * Can this check be measured on THIS template's demo? (PR-8, plan §"PR-8").
 *
 * The verdict is computed by the backend's `services/goal_applicability.py`
 * BEFORE the coach commits to a check, so they find out at authoring time
 * rather than from a report that silently left the check out (audit items
 * `RC-4` — a `signed_projection` rule that abstains on this coach's own
 * diagonal camera — and `RC-5` — a height comparison with nothing on the demo
 * to compare against).
 *
 * THREE verdicts, never two:
 *  · `can_measure`    — it will be measured on this demo.
 *  · `cannot_measure`  — it is positively known that it will not be. The
 *                        `reason_kind` says which wall it hit.
 *  · `cannot_assess`   — we do not know, because nobody has said how the demo
 *                        was filmed. **Never reported as `can_measure`.** The
 *                        camera view is only auto-detected behind a
 *                        default-OFF flag, so "unknown" is the common case on
 *                        a template whose coach declared no camera angle.
 *
 * `notice` carries the coach-facing what / why / how and is rendered VERBATIM.
 * `reason_kind` is a machine label for tests and telemetry — NEVER rendered
 * (`docs/COACH_FACING_COPY_STANDARD.md` §2).
 */
export interface GoalApplicability {
  verdict: 'can_measure' | 'cannot_measure' | 'cannot_assess';
  /** Machine label for which wall the check hit. Never rendered. */
  reason_kind: string | null;
  /** The coach-facing explanation, or null for a plain `can_measure`. */
  notice: Notice | null;
}

export interface MaterializedAnnotation {
  id: string;
  type: 'rectangle' | 'circle' | 'line';
  importance: 'low' | 'medium' | 'high' | 'critical';
  /**
   * ABSENT on a focus-area region the materialiser could not resolve to one
   * side of the body (plan §C.3a guard 2).
   *
   * A region only scores when it has body-point indices, and resolving those
   * fail-closed is PR-4's job — so the materialiser sets `tracking`,
   * `landmark_indices`, `landmark_names` and `resolution` TOGETHER or sets
   * none of them. An absent `tracking` means "not scoring yet", and every
   * consumer must read it that way: the checkpoint list says so in plain
   * words and shows no "counting in scores" badge, and the hull calculation
   * skips it. Writing `tracking: true` here without indices would promise the
   * coach a weighting that nothing applies.
   */
  tracking?: boolean;
  description: string;
  color: string;
  /** NEVER set by the materialiser — see contract note 3. */
  coordinates?: undefined;
  activeFrames?: {
    mode: 'all' | 'range';
    startFrame?: number;
    endFrame?: number;
  };
  landmark_indices?: number[];
  landmark_names?: string[];
  functional_region?: string;
  /**
   * The coach's OWN words for the body points ("lead_knee"), kept apart from
   * the anatomical `landmark_names` because every display and mirror path
   * reads the latter — plan §C.3a guard 1. Never rendered: it is the authored
   * intent, in internal vocabulary.
   */
  authored_landmark_names?: string[];
  /**
   * Present exactly when this region's body points were resolved to one side
   * and it therefore counts in the score (PR-4). See `RegionResolution`.
   */
  resolution?: RegionResolution;
  /**
   * Present exactly when the region could NOT be resolved: the coach-facing
   * what / why / how for the ask, in the backend's own words (contract note
   * 2 — rendered verbatim, never rewritten here).
   */
  notice?: Notice;
  /**
   * PR-5. What this check does when the athlete is off, in the backend's own
   * verdict — present on a materialised or accepted annotation, absent on one
   * built before PR-5 (absent renders nothing, never "not a hard limit").
   */
  gate_summary?: GateSummary;
  /**
   * PR-7. The number the coach SAID and the number their demo SHOWED, and
   * which of the two this check is scored against. Present on a goal
   * annotation built from a stated target; absent when no number was said, and
   * absent on an annotation built before PR-7 (absent renders nothing).
   */
  verbal_target?: VerbalTarget;
  /**
   * PR-8. Whether this check can actually be measured on THIS template's demo
   * — the backend's own verdict, computed before the coach commits to it.
   * Absent on an annotation built before PR-8 (absent renders nothing, and is
   * never read as "can measure").
   */
  applicability?: GoalApplicability;
  /**
   * Join key back to the proposed item this annotation came from
   * (`suggested_goal:{i}` / `body_part_emphasis:{i}` — the same strings the
   * notices carry, compared whole, never parsed). Stamped by the backend on
   * every materialised annotation since PR-7; optional only so an older
   * response still type-checks.
   */
  source_ref?: string;
  notes?: string;
  target_phase?: string | null;
  spatial_goal?: SpatialGoal;
  timing_goal?: TimingConfig;
  axis_goal?: AxisConfig;
  joint_angle_goal?: JointAngleConfig;
  /** Which materialiser built this annotation (D10 version stamp). */
  materializer_version?: string;
  /** Coach-authored "why this matters", passed verbatim to the narrative. */
  purpose_text?: string | null;
  /** Where this annotation came from — opaque to the frontend. */
  provenance?: Record<string, unknown>;
  /** Which goal key carries the config, as the backend names it. */
  goal_type?: string;
  /** The coach's own label for this check. */
  label?: string;
}

/**
 * Which suggestions to materialise.
 *
 * Indices refer to positions in `TeachingVideoAnalysis.suggested_goals` and
 * `.body_part_emphasis` — the same positions `source_ref` encodes. The coach's
 * phases ride along because the materialiser owns phase-name reconciliation
 * now (it moved out of the client with the rest of the converter), and at
 * template-creation time there is no saved template to read them from.
 */
export interface MaterializeRequest {
  goal_indices?: number[];
  emphasis_indices?: number[];
  /** The coach-defined phases to reconcile each item's phase NAME against. */
  template_phases?: Phase[];
  /**
   * PR-7. Which of the two numbers wins for a goal whose stated target and
   * demonstrated value disagree — keyed by the goal's index in
   * `suggested_goals`, AS A STRING (`{ "0": false }`), the same index
   * `goal_indices` and `source_ref` carry.
   *
   * Absent, or `true`: the number the coach SAID is the scoring reference
   * (the default — the 2026-05-15 locked decision §3). `false`: score against
   * the value the coach's own demo showed instead.
   *
   * Switching RE-MATERIALISES the goal: the backend rebuilds the annotation
   * against the other number and returns it, and the client replaces the
   * annotation it already appended rather than adding a second one (see
   * `replaceMaterializedAnnotations` in `utils/materialization.ts`).
   *
   * This lives on the REQUEST and on the suggestion, never on a goal config —
   * a backend pin test asserts it stays absent from both configs (plan
   * §C.3b(3): "`verbal_target_priority` is never added to a goal config").
   */
  verbal_target_priority?: Record<string, boolean>;
}

/** Response from POST /api/teaching-videos/{id}/materialize. */
export interface MaterializationResult {
  /** Ready-to-save, canvas-less. Append to the template as they are. */
  annotations: MaterializedAnnotation[];
  /** Every coach-facing sentence about this run. Rendered verbatim. */
  notices: Notice[];
  /** Items that did NOT become checks. Never silently absent (E2E-5/E2E-6). */
  dropped: DroppedItem[];
  materializer_version: string;
  summary: MaterializationSummary;
}
